/**
=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useMemo, ReactNode } from "react";

// react-chartjs-2 components
import { Bar } from "react-chartjs-2";

// @mui material components
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

// Material Dashboard 2 PRO React TS components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// VerticalBarChart configurations
import configs from "examples/Charts/BarCharts/VerticalBarChart/configs";

// Custom components
import CardSkeleton from "examples/Cards/CardSkeleton";
import CardError from "examples/Cards/CardError";

// Material Dashboard 2 PRO React TS Base Styles
import colors from "assets/theme/base/colors";

// Declaring props types for HorizontalBarChart
interface Props {
  icon?: {
    color?: "primary" | "secondary" | "info" | "success" | "warning" | "error" | "light" | "dark";
    component: ReactNode;
  };
  title?: string;
  description?: string | ReactNode;
  height?: string | number;
  chart: {
    labels: string[];
    datasets: {
      label: string;
      color: "primary" | "secondary" | "info" | "success" | "warning" | "error" | "light" | "dark";
      data: number[];
    }[];
  };
  stacked?: boolean;
  legendPosition?: "top" | "left" | "bottom" | "right" | "chartArea";
  isLoading?: boolean;
  isError?: boolean;
  errorMessage?: string;
  onRetry?: () => void;
  [key: string]: any;
}

function VerticalBarChart({
  icon,
  title,
  description,
  height,
  chart,
  stacked,
  legendPosition,
  isLoading = false,
  isError = false,
  errorMessage,
  onRetry,
}: Props): JSX.Element {
  const chartDatasets = chart.datasets
    ? chart.datasets.map((dataset, i) => ({
        ...dataset,
        weight: 5,
        borderWidth: 2,
        borderColor: colors.dark.main,
        borderRadius: i === chart.datasets.length - 1 ? 4 : 0,
        backgroundColor: colors[dataset.color]
          ? colors[dataset.color || "dark"].main
          : colors.dark.main,
        fill: false,
        maxBarThickness: 35,
      }))
    : [];

  const { data, options } = configs(
    chart.labels || [],
    chartDatasets,
    stacked,
    undefined,
    legendPosition
  );

  const chartContent = useMemo(
    () => (
      <MDBox height={height}>
        <Bar data={data} options={options} />
      </MDBox>
    ),
    [chart, height, legendPosition]
  );

  const renderChart = (
    <MDBox py={0.5}>
      <Accordion defaultExpanded sx={{ boxShadow: "none" }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          {title || description ? (
            <MDBox display="flex" px={description ? 1 : 0} pt={description ? 1 : 0} mt={-2}>
              {icon?.component && (
                <MDBox
                  width="3rem"
                  height="3rem"
                  bgColor={icon.color || "info"}
                  variant="gradient"
                  coloredShadow={icon.color || "info"}
                  borderRadius="xl"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  color="white"
                  mr={2}
                >
                  <Icon fontSize="medium">{icon?.component}</Icon>
                </MDBox>
              )}
              <MDBox>
                {title && <MDTypography variant="h6">{title}</MDTypography>}
                <MDTypography component="div" variant="button" color="text">
                  {description}
                </MDTypography>
              </MDBox>
            </MDBox>
          ) : null}
        </AccordionSummary>
        <AccordionDetails>{chartContent}</AccordionDetails>
      </Accordion>
    </MDBox>
  );

  if (isLoading) {
    return <CardSkeleton height={height} />;
  }

  if (isError) {
    return <CardError height={height} message={errorMessage} onRetry={onRetry} />;
  }

  return title || description ? <Card>{renderChart}</Card> : renderChart;
}

// Declaring default props for VerticalBarChart
VerticalBarChart.defaultProps = {
  icon: { color: "info", component: "" },
  title: "",
  description: "",
  height: "19.125rem",
  legendPosition: "top",
  isLoading: false,
  isError: false,
};

export default VerticalBarChart;
